
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { Loader2, Pencil } from "lucide-react";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

import {
  useGetMyProfileQuery,
  useUpdateProfileMutation,
} from "@/features/api/profileApi";

/* ================= MEDIA URL ================= */
const resolveMediaUrl = (url = "") => {
  if (!url) return "/image/avatar.jpg";
  if (url.startsWith("http")) return url;
  return url;
};

/* ================= FORM NORMALIZER ================= */
const getInitialFormData = (profile, authUser) => ({
  name: profile?.name || authUser?.name || "",
  bio: profile?.bio || "",
  phone:
    authUser?.phone ||
    authUser?.mobile ||
    authUser?.tel ||
    profile?.phone ||
    profile?.mobile ||
    profile?.tel ||
    "",
});

export default function Profile() {
  const { user: authUser } = useSelector((state) => state.auth);

  const { data, isLoading, refetch } = useGetMyProfileQuery();
  const [updateProfile, { isLoading: saving }] =
    useUpdateProfileMutation();

  const profile = data?.user || data;

  const [isEdit, setIsEdit] = useState(false);

  const [form, setForm] = useState({
    name: "",
    bio: "",
    phone: "",
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [originalImage, setOriginalImage] = useState("");
  const [removeAvatar, setRemoveAvatar] = useState(false);

  /* ================= LOAD PROFILE (SAFE) ================= */
  useEffect(() => {
    if (!profile || isEdit) return;

    setForm(getInitialFormData(profile, authUser));

    const img = resolveMediaUrl(profile.photoUrl);
    setImagePreview(img);
    setOriginalImage(img);
  }, [profile, authUser, isEdit]);

  /* ================= HANDLERS ================= */
  const handleChange = (e) => {
    const { id, value } = e.target;
    setForm((prev) => ({ ...prev, [id]: value }));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";

    if (!file || !file.type.startsWith("image/")) {
      toast.error("Please select a valid image");
      return;
    }

    setImageFile(file);
    setRemoveAvatar(false);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview("");
    setRemoveAvatar(true);
  };

  const cancelEdit = () => {
    setIsEdit(false);
    setImageFile(null);
    setRemoveAvatar(false);
    setImagePreview(originalImage);
    setForm(getInitialFormData(profile, authUser));
  };

  const handleSave = async () => {
    const fd = new FormData();

    fd.append("name", form.name);
    fd.append("bio", form.bio);
    fd.append("phone", form.phone);

    if (imageFile) {
      fd.append("profilePhoto", imageFile);
    }

    if (removeAvatar) {
      fd.append("removeAvatar", "true");
    }

    try {
      await updateProfile(fd).unwrap();
      toast.success("Profile updated successfully");
      setIsEdit(false);
      setImageFile(null);
      setRemoveAvatar(false);
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Update failed");
    }
  };

  /* ================= LOADER ================= */
  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">My Profile</h1>

        {!isEdit && (
          <Button variant="outline" onClick={() => setIsEdit(true)}>
            <Pencil className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Details</CardTitle>
              <CardDescription>
                {isEdit
                  ? "Edit your personal information"
                  : "Your personal information"}
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-4">
              <ProfileField
                label="Full Name"
                id="name"
                value={form.name}
                isEdit={isEdit}
                onChange={handleChange}
              />

              <ProfileField
                label="Bio"
                id="bio"
                value={form.bio}
                isEdit={isEdit}
                textarea
                onChange={handleChange}
              />

              <ProfileField
                label="Phone"
                id="phone"
                value={form.phone}
                isEdit={isEdit}
                onChange={handleChange}
              />
            </CardContent>
          </Card>

          {isEdit && (
            <div className="flex gap-3">
              <Button variant="secondary" onClick={cancelEdit}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          )}
        </div>

        {/* RIGHT */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Image</CardTitle>
            </CardHeader>

            <CardContent className="space-y-4 text-center">
              <Avatar className="h-32 w-32 mx-auto">
                <AvatarImage src={imagePreview} />
                <AvatarFallback>
                  {form.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>

              {isEdit && (
                <>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                  />

                  {(imagePreview || originalImage) && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={removeImage}
                    >
                      Remove Image
                    </Button>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Account Info</CardTitle>
            </CardHeader>

            <CardContent className="space-y-2 text-sm">
              <p>
                <strong>Email:</strong> {authUser?.email || "—"}
              </p>
              <p>
                <strong>Role:</strong>{" "}
                {authUser?.role?.toUpperCase() || "—"}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

/* ================= FIELD ================= */
const ProfileField = ({
  label,
  value,
  isEdit,
  id,
  onChange,
  textarea,
}) => (
  <div className="space-y-1">
    <Label htmlFor={id}>{label}</Label>
    {isEdit ? (
      textarea ? (
        <Textarea id={id} value={value} onChange={onChange} />
      ) : (
        <Input id={id} value={value} onChange={onChange} />
      )
    ) : (
      <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">
        {value || "—"}
      </p>
    )}
  </div>
);
